import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-checkbox-circle-line",
    title: "只整理可核实的公开信息",
    text: "每条事件记录都会标注信息来源与时间点，未获证实的内容会单独说明，不与已确认的事实混在一起。",
  },
  {
    icon: "ri-scales-3-line",
    title: "给当事方留出回应空间",
    text: "只要当事方发布了公开回应，我们都会同步补充到记录末尾，并明确标注回应的时间与渠道。",
  },
  {
    icon: "ri-forbid-2-line",
    title: "不放大无依据的猜测",
    text: "对于没有实质信息支撑的传闻，我们只做有必要的说明，不会为了流量反复放大或制造对立。",
  },
  {
    icon: "ri-refresh-line",
    title: "有进展就更新，没进展就停更",
    text: "当话题没有新的关键信息时，我们会停止追加内容，避免用重复的表达占据读者的时间。",
  },
];

const values = [
  { icon: "ri-time-line", title: "省时间", text: "把一件事的来龙去脉压进几分钟的阅读里。" },
  { icon: "ri-links-line", title: "有脉络", text: "按时间线组织信息，而不是零散段落的堆叠。" },
  { icon: "ri-eye-line", title: "看得清", text: "事实与传闻分开标注，读者自己判断。" },
];

const voices = [
  {
    quote:
      "平时刷到热搜只看到片段，来这里能一次把前因后果读完，不用再翻十几个帖子拼线索。",
    name: "小满",
    role: "通勤路上常看",
  },
  {
    quote:
      "喜欢它把官方回应单独列出来，很多账号只会截最刺激的那一段，这里相对克制。",
    name: "阿哲",
    role: "关注娱乐话题的读者",
  },
  {
    quote:
      "分类做得清楚，想只看社会话题就点对应的分区，热榜也能直接看出哪些在涨、哪些在退。",
    name: "Yuki",
    role: "内容从业者",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 | 52吃瓜网的内容原则与定位",
    description:
      "了解52吃瓜网的网站定位、内容整理原则与用户口碑。我们专注把分散的热点线索整理成有脉络的事件记录，坚持核实来源、同步回应、拒绝传谣。",
    keywords: "关于52吃瓜网,内容原则,网站定位,热点整理,用户口碑",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 52吃瓜网",
        description: "52吃瓜网的网站定位、内容原则与用户口碑介绍。",
        url: "https://kimchy.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "52吃瓜网",
        url: "https://kimchy.cn/",
        description: "把分散在各平台的热点线索整理成完整事件记录的中文资讯门户。",
        email: "contact@52chigua.example.com",
        slogan: "今日热门，一站看清。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://kimchy.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://kimchy.cn/about" },
        ],
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        <div className="mt-5 grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              关于 52吃瓜网
            </h1>
            <p className="mt-4 text-sm leading-loose text-foreground-600 font-label">
              52吃瓜网是一个专注"今日热门与全网热议事件汇总"的资讯门户。我们不生产耸动的标题，而是把散落在各个平台上的线索，按时间顺序整理成一条可以读完的事件记录，让关注热点的人用更少的时间获得更完整的上下文。
            </p>
            <p className="mt-3 text-sm leading-loose text-foreground-600 font-label">
              从娱乐八卦到社会奇闻，从网红动态到影视综艺，我们只做一件事：让每一件被讨论的事，都有一条清晰的来龙去脉。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/hot"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
              >
                查看今日热榜
              </Link>
              <Link
                to="/contact"
                className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                联系我们
              </Link>
            </div>
          </div>
          <div className="relative h-56 overflow-hidden rounded-lg bg-background-200 md:h-72">
            <img
              src="https://readdy.ai/api/search-image?query=stylized%20abstract%20editorial%20workspace%20scene%20with%20floating%20news%20cards%20and%20warm%20lamp%20glow%2C%20coral%20red%20and%20golden%20amber%20accents%20on%20cream%20background%2C%20clean%20minimal%20magazine%20illustration%2C%20soft%20grain%2C%20no%20people&width=900&height=600&seq=chigua-about-01&orientation=landscape"
              alt="52吃瓜网编辑部整理热点事件的工作场景插画"
              title="52吃瓜网 今日热门事件汇总"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {values.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-2xl text-accent-700">
                <i className={item.icon}></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                {item.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600 font-label">
                {item.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
          我们的内容原则
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 font-label">
          热点话题传播得很快，但我们更在意它被讲清楚的方式。以下四条是我们整理每篇事件记录时都会遵守的底线。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {principles.map((item, index) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                <i className={item.icon}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  <span className="mr-2 text-xs font-label text-primary-500">
                    0{index + 1}
                  </span>
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600 font-label">
                  {item.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 py-10 md:px-8 md:py-14">
        <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
          读者怎么说
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 font-label">
          来自常来逛的读者的一些反馈，我们把它当作一直更新的理由。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {voices.map((voice) => (
            <figure
              key={voice.name}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <i className="ri-double-quotes-l text-2xl text-accent-400"></i>
              <blockquote className="mt-3 flex-1 text-sm leading-loose text-foreground-700 font-label">
                {voice.quote}
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-3 border-t border-background-100 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                  {voice.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-label font-medium text-foreground-900">
                    {voice.name}
                  </span>
                  <span className="block text-xs font-label text-foreground-400">
                    {voice.role}
                  </span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="px-4 pb-12 md:px-8 md:pb-16">
        <div className="flex flex-col gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              有线索、建议或合作意向？
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600 font-label">
              邮箱联系：contact@52chigua.example.com　·　也可直接在联系页面提交。
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
          >
            前往联系页面
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </>
  );
}